import Index from '@/pages/index'
import Main from '@/pages/overview/index'
import Side from '@/pages/overview/side'
import { matchMicroAppRoute } from '@/micro-app'
import router from '@/router'
import VueI18n from '@/i18n/locale'
import lodash from 'lodash'
import { SUMMARY_KEY } from '@/config/constants'
import MENU_ICONS_MAPS, { defaultIcon } from '@/config/menu-icons-map'
import { useMenuStore } from '@/store'
import { trackEvent } from '@/plugins/plugin-init-omega'
import { gtrTrackCommonData } from '@/utils/gattaran-track-event'
import { matchRoute, cached } from '@/utils/utils.js'

const matchRouteCached = cached(matchRoute)

// import WebWorker from '@/utils/workers';
function isIcon(str) {
  if (!str) return
  return (
    str.includes('icon-') || // 普通 icon 库
    str.includes('gattaran-') || // gattatan 库
    str.includes('pb-icon-') || // http://pebble.design.intra.didiglobal.com/vue/#/zh-CN/component/icon
    str.includes('pb_icon-') // http://pebble.design.intra.didiglobal.com/vue/#/zh-CN/component/pb-icon
  )
}

function isPath(str) {
  if (!str) return
  return str.includes('/')
}

/**
 * 匹配本地路由
 */
export function matchLocalComponent(name) {
  const menuStore = useMenuStore()
  return menuStore.getRouteFlattenObject[name]
}

/**
 * 获取第一有权限的叶子节点
 * @param nodes
 * @returns {null}x`
 */
function getFirstAuthRoute(nodes = []) {
  let first = null
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].isAuth) {
      const sonNodes = nodes?.[i]?.sonNodes || []
      for (let j = 0; j < sonNodes.length; j++) {
        if (sonNodes?.[i]?.isAuth) {
          first = sonNodes?.[i]
          break
        }
      }
      break
    }
  }
  const { matchPath, matchApp } = getPath(first)
  return {
    matchPath,
    matchApp,
  }
}

/**
 * 匹配微前端子应用前缀
 * @param path
 * @returns {{matchPath: *, matchApp}}
 */
function getMatchPathByPath(path) {
  const matchApp = matchMicroAppRoute(path)
  return {
    matchApp,
    matchPath: matchApp ? matchApp.prefix + path : path,
  }
}

/**
 * 解析nodeKey
 * @param key
 * @returns {{next: *, pre: *, isIcon: *, isPath: *}}
 */
function transformKey(key) {
  const [pre, next] = key?.split('$$') || ['', '']
  return {
    pre,
    next,
    isIcon: isIcon(next),
    isPath: isPath(next),
  }
}

/**
 * 通过nodeKey获取全量的路由（微前端项目增加前缀）
 * @param key
 * @returns {undefined|{matchPath: *, matchApp}}
 */
export function getMatchPathByNodeKey(key) {
  const { next } = transformKey(key)
  if (isPath(next)) {
    return getMatchPathByPath(next)
  } else {
    return undefined
  }
}

/**
 * 解析路由参数key，转换path -> 微前端path
 * @returns {*|string}
 * @param route
 */
function getPath(route) {
  const { next } = transformKey(route?.key)
  const path = route?.path ? route?.path : isPath(next) ? next : `/${route?.key}`
  return getMatchPathByPath(path)
}

// let ICON_MAPS = {}; // 测试
function getIcon(i) {
  const { next } = transformKey(i?.key)
  const hasNewIcon = transformKey(MENU_ICONS_MAPS?.[i.key])?.next
  return hasNewIcon || (i.icon ? i.icon : isIcon(next) ? next : defaultIcon)
}

/**
 * 获取唯一的key
 * 作为导航的index
 *  path / name / key
 * @param i
 */
function makeUniqueKey(i) {
  // 路由的name为唯一值 ，采用menu对象的key值
  // 如果接入微前端采用path作为name,
  // 导航匹配需要唯一的key，
  //    服务端下发menuList是有唯一key的
  //    但是微前端接入的子应用中未在menuList注册的次级路由是没有唯一key的， 子应用前缀 + 路由path 作为唯一key
  //    todo 优化（子应用路由切换的时候需要透传mathKeys，就是需要导航定位的，把父级key也透传）
  const { next } = transformKey(i?.key)
  const { matchPath, matchApp } = getPath(i)
  return {
    uniqueKey: (isPath(next) ? matchPath : i.key || i.name).trim(),
    matchApp,
  } // 配置的key有的不规范有空格。。
}

/**
 * node节点转route对象
 * sonNodes -> children 递归处理
 * @param i
 * @param deep
 * @param parent
 * @returns {{path, component: {components: {Side: function(): Promise<{readonly default?: {components: {SideProducts: {components: {ApplyRoles: {data(): {secondary: boolean, drawer: boolean, disabled: boolean, loading: boolean, roleList: []}, watch: {drawer: {handler: function(*=): void}}, methods: {getText(*): string, toApply(*): void, isDisabled(*): boolean}, mounted(), props: {nodeId: {default: number, type: Number | NumberConstructor}}}}, data(): {showApplyDrawer: boolean, visible: boolean, nodeId: number}, methods: {toAppyOrSee(*=): void, getButtonTxt(*): string, toModule(*=): void}, props: {sides: {default: function(): [], type: Array | ArrayConstructor}}}, CommonBanner: {components: {ApplyRoles: {data(): {secondary: boolean, drawer: boolean, disabled: boolean, loading: boolean, roleList: []}, watch: {drawer: {handler: function(*=): void}}, methods: {getText(*): string, toApply(*): void, isDisabled(*): boolean}, mounted(), props: {nodeId: {default: number, type: Number | NumberConstructor}}}}, data(): {nodeId: number}, methods: {toApply(): void}, props: {content: {default: function(), type: Object | ObjectConstructor}}}}, data(): {isOwn: boolean, sides: [], content: {}}, mounted(): void}}>}, data(): {isSummary: boolean, title: string}, watch: {"$route.meta": {handler(*): void, deep: boolean, immediate: boolean}}}, hidden: (boolean|*), children: (*|*[]), meta: {parentName: (*|string), isAsync: boolean, icon: (*|string), matchedKeys: T[], linkType: string, title: string}, name: *}}
 */
export function nodeToRoute(i, deep = 0, parent = { path: '' }) {
  const pNext = parent?.path
  // key格式：  xxxx$$xxxxx  （$$后面的字符串有两种情况：icon 或者 路径path
  const { next } = transformKey(i?.key)
  // 解析path
  const { matchPath } = getPath(i)
  // 唯一key
  const { uniqueKey, matchApp = {} } = makeUniqueKey(i)
  // 保存每一级匹配到的keys，方便选择父节点、祖父节点等
  const matchedKeys = (parent?.matchedKeys || []).concat([uniqueKey])
  // 判断是http(s)链接
  const httpUrl = next && /^(https?:)/.test(next) ? next : ''
  // 将服务端下发端menu对象的某些需要的字段放到路由对象的meta里，方便取用。
  // isAuth 是否有权限，isHide（是否隐藏 ，针对顶部隐藏side的，默认false），isApply（概览是否展示查看权限按钮）
  const metaExtend = lodash.pick(i, ['nodeId', 'isAuth', 'nodeType', 'remark', 'key', 'isApply'])
  // 隐藏逻辑
  // isHide后端返回的菜单隐藏的字段，hidden前端路由设置的隐藏字段， // isAuth有无权限
  const hidden = i.isHide || i.hidden
  // icon
  // 有限匹配新icon http://10.190.13.162/#/user/8090/repo/2453
  // 没有匹配旧gtr-icon
  const icon = deep === 1 ? getIcon(i) : ''
  // if (deep === 2 && !!i.key) {
  //   ICON_MAPS[i.key] = i.name;
  // }
  return {
    ...lodash.pick(i, ['redirect']),
    name: uniqueKey,
    path: matchPath,
    component: Main,
    hidden, // 无权限的设置为隐藏
    meta: {
      httpUrl,
      isAsync: !!i.key, // 服务端下发的路由标志
      title: i.name || '', // 路由标题，用作前端展示
      appName: matchApp.name,
      icon, // 菜单icon
      parentName: pNext || '', // 父节点的名称
      matchedKeys, // 保存每一级的keys，方便选择父节点、祖父节点等
      ...(i?.meta || {}),
      ...metaExtend,
    },
    children: transformList(i?.sonNodes || [], deep + 1, { ...i, matchedKeys, path: matchPath }),
  }
}

/**
 * 服务端下发menuList处理
 * 1、转vue路由对象
 * 2、每个二级路由层级动态添加 /x-side/summary概述页面
 * 3、二级路由重定向逻辑
 * 4、路由无关参数添加到mate里
 * @param list
 * @param deep
 * @param parent
 * @returns {*}
 */
export function transformList(list = [], deep = 0, parent = undefined) {
  const pNext = parent?.path
  if (!Array.isArray(list)) {
    console.error('-----start 不是数组')
    list = lodash.isObject(list) ? Object.values(list) : []
  }
  if (deep === 0) {
    list.forEach(l => {
      // 子系统没权限，隐藏顶部导航菜单
      if (l?.sonNodes?.length === 0) {
        l.hidden = true
      }
    })
  }
  if (deep === 1 && parent) {
    // 获取第一个有权限的叶子节点
    const { /** matchPath,**/ matchApp } = getFirstAuthRoute(list)
    // 重定向路由
    list.unshift({
      path: pNext,
      name: pNext + '/side_redirect',
      // redirect: matchPath || `${pNext}/summary`, // 需求跳转第一个匹配的有权限的节点，暂时注释调
      redirect: `${pNext}/summary`, // 默认跳转概览页面
      hidden: true,
    })
    const summaryKey = parent.key + SUMMARY_KEY
    const matchedKeys = (parent?.matchedKeys || []).concat([summaryKey]) // 链路
    if (matchApp && matchApp.prefix) {
      // 子应用下的注册的path /gtr_xxx/* 的都匹配
      list.push({
        path: matchApp.prefix + '/*',
        name: matchApp.name,
        component: Main,
        hidden: true, // menuList未返回路由均无需在侧边展示
        meta: {
          title: matchApp.title, // todo 子应用未注册路由的名称？
          parentName: pNext || '',
          keys: matchedKeys,
        },
      })
    }
    // 防止重复注册
    const has = list.find(i => i.name === summaryKey)
    if (!has) {
      // 动态添加概述
      !parent.isHide &&
        list.push({
          path: pNext + '/summary',
          name: summaryKey,
          component: Side,
          meta: {
            key: summaryKey,
            title: VueI18n.t('Gattaran_Homepage__oGEC'),
            icon: 'gattaran-gailan',
            isSummary: true,
            parentName: pNext || '',
            keys: matchedKeys,
          },
        })
    }
  }
  return list.map(i => {
    // if (deep === 1 && i.isAuth !== undefined) {
    //   i.isAuth = true;
    // }
    return nodeToRoute(i, deep, parent)
  })
}

/**
 * 将原始routeList拍平并转对象
 * @param list
 * @returns {*}
 */
export function flattenAndObject(list) {
  function toFlatAndObj(tList) {
    return tList.reduce((pre, next) => {
      const { children, component, ...extend } = next
      pre[next.name] = extend
      if (Array.isArray(children) && children.length > 0) {
        pre = { ...pre, ...toFlatAndObj(children) }
      }
      return pre
    }, {})
  }

  return toFlatAndObj(list)
}

/**
 * 添加动态路由
 * @param route
 * @param newList
 */
export function addRoute(route = router, newList) {
  const hasGtrAsyncRouter = route?.getRoutes().find(i => i.name === 'gtr-async-router')
  if (hasGtrAsyncRouter) return // 防止重复注册 带优化
  route.addRoute({
    path: '/',
    component: Index,
    children: [
      {
        name: 'gtr-async-router',
        path: '/gtr-async-router',
        hidden: true,
      },
      ...newList,
    ],
  })
}

// 增加缓存
let routes = null
let routesMap = {}

function getRouterInfo() {
  if (!routes) {
    routes = router.getRoutes()
    // console.log(routes.map(o=>o.path))
    routesMap = routes.reduce((pre, next) => {
      if (next.path) {
        pre[next.path] = next
      }
      return pre
    }, {})
  }
  return {
    routes,
    routesMap,
  }
}

/**
 * 微应用次级路由，在主应用未注册的情况下匹配对应的主应用。
 * 需要严格按照多级路由的命名规范。子应用与主应用注册的路由存在最长子序列即可匹配。
 * @param path 目标路径
 * @param isJump 是否跳过模糊匹配
 * @returns {{}|*}
 */
export function subMatchMenu(path, isJump) {
  console.log('[log]=> path 1', path)
  const { routes, routesMap } = getRouterInfo()
  const _matchRoute = matchRouteCached(path, routesMap)
  if (_matchRoute) {
    return _matchRoute
  }
  console.warn('子系统注册不规范，进行模糊匹配。刷新页面有可能菜单匹配异常，需自行检查。', path)
  trackEvent('tech_gattaran_router_match_error_bt', '加特兰子系统路由不规范埋点', {
    ...gtrTrackCommonData(),
    path,
  })
  if (isJump) return {} // 无刷新切换，不需要对次级路由进行模糊匹配
  // 最长子序列模糊匹配
  const paths = path.split('/').filter(i => i)
  const len = routes.length
  // 遍历已注册菜单的路由列表
  for (let i = 0; i < len; i++) {
    const r = routes[i]
    const rP = r?.path
    const rPaths = rP.split('/').filter(i => i)
    if (!rP || !path) continue
    const minLen = rPaths.length < paths.length ? rPaths.length : paths.length
    let l1 = 0
    let l2 = 0
    while (l1 < minLen - 1 && l2 < minLen - 1) {
      const o = rPaths?.slice(0, l1 > 0 ? -l1 : undefined)
      const t = paths?.slice(0, l2 > 0 ? -l2 : undefined)
      if (!o.toString() || !t.toString()) return {}
      if (o.toString() !== t.toString()) {
        if (o.length < t.length) {
          l2++
        }
        if (o.length > t.length) {
          l1++
        }
        if (o.length === t.length) {
          l1++
          l2++
        }
      } else {
        return r
      }
    }
  }
  return {}
  // 打包部署后静态文件都会上传sdn，造成静态资源不同源（webWorker跨域情况是不允许的，具体链接跨域通过fetch请求加载可以解决，但是需要改造）
  // try {
  //   const data = await WebWorker.postMessage('getMatchMenu', [
  //     routes.map((i) => {
  //       return lodash.pick(i, ['name', 'meta', 'path', 'redirect']);
  //     }),
  //     path,
  //   ]);
  //   return data.data;
  // } catch (e) {
  //   console.warn(e);
  //   return null;
  // }
}
